import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus, RefreshCw, Snowflake } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { ProspectCard } from "@/components/ProspectCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const COLUMNS = [
  { key: "sent", label: "Link sent", hint: "Created, not opened yet" },
  { key: "opened", label: "Opened the tour", hint: "They looked" },
  { key: "unlocked", label: "Unlocked a price", hint: "They get it — call now" },
  { key: "walkthrough", label: "Wants a walkthrough", hint: "They asked to talk" },
  { key: "contacted", label: "Contacted", hint: "You've reached out" },
  { key: "won", label: "Won", hint: "On the platform" },
  { key: "lost", label: "Passed", hint: "Not now" },
];

export default function Prospects() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const [showLost, setShowLost] = useState(false);
  const [form, setForm] = useState({ dealer_name: "", city: "", state: "", website: "" });

  const load = () => api.get("/platform/prospects").then(({ data }) => setData(data))
    .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't load the pipeline"));
  useEffect(() => { load(); }, []);

  const replace = (card) => setData((d) => ({ ...d, prospects: d.prospects.map((x) => (x.key === card.key ? card : x)) }));

  const patch = async (p, body) => {
    setBusy(p.key);
    try {
      const { data: card } = await api.patch(`/platform/prospects/${p.key}`, body);
      replace(card);
      if (body.status) toast.success(`${card.dealer_name} → ${body.status === "contacted" ? "contacted" : body.status}`);
      else if (body.note) toast.success("Note saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const act = async (p, path, msg) => {
    setBusy(p.key);
    try {
      const { data: card } = await api.post(`/platform/prospects/${p.key}/${path}`);
      replace(card);
      toast.success(msg(card));
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const addProspect = async () => {
    if (!form.dealer_name.trim()) { toast.error("Type the dealership's name"); return; }
    setBusy("new");
    try {
      await api.post("/platform/demo/invites", form);
      setForm({ dealer_name: "", city: "", state: "", website: "" });
      await load();
      toast.success(`${form.dealer_name} added with a branded link`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  const copy = (text) => {
    if (!navigator.clipboard?.writeText) { toast.info(text); return; }
    navigator.clipboard.writeText(text).then(() => toast.success("Link copied")).catch(() => toast.info(text));
  };

  const columns = COLUMNS.filter((c) => c.key !== "lost" || showLost);

  return (
    <div className="space-y-6" data-testid="prospects-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">Prospects</h1>
          <p className="text-sm text-slate-400 mt-1">
            Every dealership that has touched Vinconex — branded links you sent, people who found the tour, contact messages and signups.
            Cards move right on their own; amber means nothing has happened for two days and you haven't called.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {data?.cold > 0 && (
            <span className="inline-flex items-center gap-1.5 text-xs text-amber-400 font-mono-tech" data-testid="prospects-cold-count">
              <Snowflake className="h-3.5 w-3.5" /> {data.cold} going cold
            </span>
          )}
          <Button onClick={load} variant="outline" data-testid="prospects-refresh-btn" className="border-slate-700 bg-slate-900/50 hover:bg-slate-800 text-slate-200">
            <RefreshCw className="mr-2 h-4 w-4" /> Refresh
          </Button>
        </div>
      </div>

      <div className="rounded-xl border border-[#2A2E3D] bg-[#12141C] p-4">
        <div className="grid sm:grid-cols-12 gap-2">
          <Input value={form.dealer_name} onChange={(e) => setForm({ ...form, dealer_name: e.target.value })} placeholder="Dealership name"
                 data-testid="prospect-new-name" className="sm:col-span-4 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
          <Input value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} placeholder="their website (optional)"
                 data-testid="prospect-new-website" className="sm:col-span-3 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
          <Input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City"
                 data-testid="prospect-new-city" className="sm:col-span-2 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
          <Input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="PA"
                 data-testid="prospect-new-state" className="sm:col-span-1 bg-slate-950 border-slate-800 focus:border-[#0A8CF2] text-slate-100" />
          <Button onClick={addProspect} disabled={!!busy} data-testid="prospect-new-btn" className="sm:col-span-2 bg-[#0A8CF2] hover:bg-[#3AA3FF] font-bold">
            {busy === "new" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="mr-1.5 h-4 w-4" /> Add prospect</>}
          </Button>
        </div>
        <p className="text-xs text-slate-500 mt-2">Adding a prospect creates their branded demo link straight away — copy it from the card and send it.</p>
      </div>

      {!data ? (
        <div className="flex items-center justify-center py-24"><Loader2 className="h-8 w-8 animate-spin text-[#0A8CF2]" /></div>
      ) : (
        <>
          <div className="flex flex-col sm:flex-row gap-4 sm:overflow-x-auto pb-4" data-testid="prospects-board">
            {columns.map((c) => {
              const cards = data.prospects.filter((p) => p.stage === c.key);
              return (
                <div key={c.key} className={`w-full sm:w-[280px] shrink-0 ${cards.length === 0 ? "hidden sm:block" : ""}`} data-testid={`prospect-column-${c.key}`}>
                  <div className="flex items-baseline justify-between gap-2 mb-2">
                    <h2 className="font-heading text-base md:text-lg font-bold">{c.label}</h2>
                    <span className="text-xs font-mono-tech text-slate-500" data-testid={`prospect-count-${c.key}`}>{cards.length}</span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-600 font-mono-tech mb-2">{c.hint}</div>
                  <div className="space-y-2">
                    {cards.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-[#2A2E3D] p-4 text-center text-xs text-slate-600">empty</div>
                    ) : cards.map((p) => (
                      <ProspectCard key={p.key} p={p} busy={busy} onCopy={copy} onPatch={patch}
                                    onLink={(x) => act(x, "link", (card) => `Branded link ready for ${card.dealer_name}`)}
                                    onDetect={(x) => act(x, "detect-platform", (card) => `${card.dealer_name}: ${card.inventory_platform || "nothing found"}`)} />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
          <button type="button" onClick={() => setShowLost((s) => !s)} data-testid="prospects-toggle-lost" className="text-xs text-slate-500 hover:text-white">
            {showLost ? "hide passed" : `show passed (${data.by_stage.lost || 0})`}
          </button>
        </>
      )}
    </div>
  );
}
